import { useQuery } from '@tanstack/react-query';
import { shadeColour } from '@/lib/shades';
import type { DesignParameter, LayerSpec, MarginTrace } from '@/types';

/**
 * A published design as the live pages read it: the GLB, and the metadata
 * document beside it (what each part of the GLB is, and the tooth). Shared by
 * the dentist's review page and the patient's page.
 */

/** metadata.json beside the GLB, as the server's publish step writes it. */
export interface DesignMetadata {
  tooth?: string;
  /** Prescribed material and shade, as the desktop sent them. */
  material?: string;
  shade?: string;
  /** The crown's colour in NuCAD (#rrggbb). */
  crownColor?: string;
  layers?: {
    name: string;
    role: 'crown' | 'prep' | 'antagonist' | 'jaw';
    label: string;
    /** A crown layer's own tooth, when one link carries several crowns. */
    tooth?: string;
  }[];
  /** Every tooth on the link, the first being `tooth`. */
  teeth?: string[];
  /** The dentist the lab sent it to, as NuCAD knew them. */
  dentist?: { name?: string; clinic?: string };
  /** The margin lines the desktop traced, in the GLB's millimetre frame. */
  margins?: MarginTrace[];
  /** The design's parameters against the material's range. */
  parameters?: DesignParameter[];
}

export function useDesignMetadata(key: readonly unknown[], metadataUrl: string | null | undefined) {
  return useQuery({
    queryKey: [
      ...key,
      'metadata',
      metadataUrl ? new URL(metadataUrl, window.location.href).pathname : null,
    ],
    enabled: Boolean(metadataUrl),
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const res = await fetch(metadataUrl ?? '', { signal });
      if (!res.ok) throw new Error(`metadata ${String(res.status)}`);
      return (await res.json()) as DesignMetadata;
    },
  });
}

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * The margin lines, as the viewer draws them. A design published before the
 * server sent them has none; a malformed entry is left out rather than
 * breaking the page.
 */
export function marginsOf(metadata: DesignMetadata | undefined): MarginTrace[] {
  const margins: unknown = metadata?.margins;
  if (!Array.isArray(margins)) return [];
  return margins.flatMap((entry: unknown): MarginTrace[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const { tooth, points, closed } = entry as Record<string, unknown>;
    if ((typeof tooth !== 'string' && typeof tooth !== 'number') || !Array.isArray(points)) {
      return [];
    }
    const valid = points.filter(
      (point: unknown): point is [number, number, number] =>
        Array.isArray(point) && point.length === 3 && point.every(isNumber),
    );
    return valid.length >= 2
      ? [{ tooth: String(tooth), points: valid, closed: closed === true }]
      : [];
  });
}

/** The design parameters, for the Case details tab. Empty when the design has none. */
export function parametersOf(metadata: DesignMetadata | undefined): DesignParameter[] {
  const parameters: unknown = metadata?.parameters;
  if (!Array.isArray(parameters)) return [];
  return parameters.filter((entry: unknown): entry is DesignParameter => {
    if (typeof entry !== 'object' || entry === null) return false;
    const item = entry as Record<string, unknown>;
    return (
      typeof item.key === 'string' &&
      typeof item.label === 'string' &&
      typeof item.unit === 'string' &&
      [item.value, item.min, item.max, item.standard].every(isNumber)
    );
  });
}

/**
 * The crown's colour on screen: the tooth colour of its prescribed shade, so
 * an A2 crown looks like an A2 tooth; failing that, the colour NuCAD showed
 * it in; failing that, nothing, and the viewer uses neutral ceramic.
 */
export function crownColourOf(metadata: DesignMetadata | undefined): string | undefined {
  return shadeColour(metadata?.shade) ?? metadata?.crownColor ?? undefined;
}

/**
 * The parts of the GLB as layers: the crown, the jaw it sits on, and its
 * opponent - which starts hidden, so the first view is the crown on its prep
 * rather than through a translucent opposing arch.
 */
export function layersOf(metadata: DesignMetadata | undefined): LayerSpec[] {
  const tooth = metadata?.tooth;
  const crownLabel = tooth ? `Crown ${tooth}` : 'Crown';
  const colour = crownColourOf(metadata);
  if (!metadata?.layers?.length) {
    return [
      {
        id: 'Crown',
        kind: 'crown',
        label: crownLabel,
        opacity: 1,
        ...(tooth ? { tooth } : {}),
        ...(colour ? { colour } : {}),
      },
    ];
  }
  return metadata.layers.map((layer): LayerSpec => {
    if (layer.role === 'crown') {
      const own = layer.tooth ?? tooth;
      return {
        id: layer.name,
        kind: 'crown',
        label: own ? `Crown ${own}` : crownLabel,
        opacity: 1,
        ...(own ? { tooth: own } : {}),
        ...(colour ? { colour } : {}),
      };
    }
    return {
      id: layer.name,
      kind: layer.name === 'upper' ? 'upper' : 'lower',
      label: layer.label,
      opacity: layer.role === 'antagonist' ? 0 : 1,
    };
  });
}
